import React from "react";
import { fmt } from "@/lib/api";

/**
 * Full validation report renderer. Consumes the shape returned by
 * GET /api/statement-documents/{doc_id}/report.
 *
 * The parent decides whether it's a modal, drawer, or inline card.
 */

function Section({ title, children, testId }) {
  return (
    <div className="cert-section" data-testid={testId}>
      <div className="cert-section-title">{title}</div>
      <div className="cert-section-body">{children}</div>
    </div>
  );
}

function CheckRow({ label, ok, statement, imported, difference, testId }) {
  return (
    <div className="cert-check-row" data-testid={testId}>
      <span className={"cert-check-flag " + (ok ? "ok" : "bad")}>
        {ok ? "PASS" : "FAIL"}
      </span>
      <span className="cert-check-label">{label}</span>
      <span className="cert-check-values mono">
        stmt: {typeof statement === "number" ? statement.toLocaleString("en-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : String(statement ?? "—")}
        {imported !== undefined && imported !== null && (
          <> · imp: {typeof imported === "number" ? imported.toLocaleString("en-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : String(imported)}</>
        )}
        {difference !== undefined && difference !== null && Math.abs(Number(difference)) > 0.001 && (
          <> · Δ {typeof difference === "number" ? difference.toLocaleString("en-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : String(difference)}</>
        )}
      </span>
    </div>
  );
}

export default function StatementValidationReport({ report }) {
  if (!report) return <div className="empty">No report yet.</div>;
  const { document, extraction, validation, certification } = report;

  return (
    <div className="cert-report" data-testid="statement-validation-report">
      <div className="cert-header">
        <div>
          <div className="heading h3">{document?.file_name}</div>
          <div className="muted" style={{ fontSize: 12 }}>
            {document?.file_size ? `${(document.file_size / 1024).toFixed(1)} KB` : ""} ·
            sha256 {document?.sha256?.slice(0, 12)}…
            {" · "}Uploaded {fmt.dateTime(document?.created_at)}
          </div>
        </div>
        {certification && (
          <div className={
            "cert-verdict " +
            (certification.status === "certified_automatic" ? "ok" :
             certification.status === "certified_override" ? "warn" : "bad")
          } data-testid="cert-verdict">
            <div className="cert-verdict-emoji">
              {certification.status === "certified_automatic" ? "✅" :
               certification.status === "certified_override" ? "⚠" : "❌"}
            </div>
            <div>
              <div className="cert-verdict-label">
                {certification.status === "certified_automatic" && "Bank Automatically Certified"}
                {certification.status === "certified_override" && "Certified by Controller Override"}
                {certification.status === "validation_failed" && "Bank Certification Failed"}
                {certification.status === "extraction_failed" && "Statement Extraction Failed"}
              </div>
              <div className="cert-verdict-sub muted" style={{ fontSize: 11 }}>
                {certification.method === "automatic" ? "Automatic" : "Controller override"} ·
                {" "}{fmt.dateTime(certification.created_at)}
              </div>
            </div>
          </div>
        )}
      </div>

      {!extraction && (
        <div className="empty" style={{ marginTop: 12 }}>Pipeline has not run yet.</div>
      )}

      {extraction && !extraction.schema_valid && (
        <div className="cert-error-card" data-testid="cert-extraction-error">
          <strong>Extraction failed.</strong>
          <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
            The LLM ({extraction.model}) could not produce a schema-valid canonical
            statement. Retry the pipeline; if it keeps failing, the PDF may be a
            scanned image (OCR not supported).
          </div>
          <pre className="cert-error-detail">{extraction.error || "(no diagnostic)"}</pre>
        </div>
      )}

      {extraction?.canonical && (
        <Section title="Statement Extracted" testId="cert-extraction-block">
          <div className="cert-grid">
            <div><span className="muted">Statement window</span>
              <div className="mono">{extraction.canonical.period_start} → {extraction.canonical.period_end}</div></div>
            <div><span className="muted">Account #</span>
              <div className="mono">{extraction.canonical.account_number || "—"}</div></div>
            <div><span className="muted">Opening</span>
              <div className="mono">${fmt.currency(extraction.canonical.opening_balance)}</div></div>
            <div><span className="muted">Closing</span>
              <div className="mono">${fmt.currency(extraction.canonical.closing_balance)}</div></div>
            <div><span className="muted">Total debits</span>
              <div className="mono">${fmt.currency(extraction.canonical.total_debits)}</div></div>
            <div><span className="muted">Total credits</span>
              <div className="mono">${fmt.currency(extraction.canonical.total_credits)}</div></div>
            <div><span className="muted">DR count</span>
              <div className="mono">{extraction.canonical.debit_count}</div></div>
            <div><span className="muted">CR count</span>
              <div className="mono">{extraction.canonical.credit_count}</div></div>
          </div>
          <div className="muted" style={{ fontSize: 11, marginTop: 8 }}>
            Extractor · {extraction.extractor} · {extraction.model} ·
            attempt {extraction.attempt} · {extraction.duration_ms} ms
          </div>
        </Section>
      )}

      {validation && validation.controls && (
        <Section title="Statement Controls" testId="cert-controls-block">
          {validation?.convention?.imports_inverted_from_statement && (
            <div style={{
              padding: "8px 10px", marginBottom: 8, borderRadius: 6,
              background: "#eef4fb", border: "1px solid #b8d0ea",
              fontSize: 12, color: "#1c3d63",
            }} data-testid="cert-convention-note">
              <strong>DR/CR convention auto-normalised.</strong>{" "}
              {validation.convention.note}
            </div>
          )}
          {Object.entries(validation.controls.checks || {}).map(([key, check]) => (
            <CheckRow
              key={key}
              label={key.replace(/_/g, " ")}
              ok={check.pass}
              statement={check.statement}
              imported={check.imported}
              difference={check.difference}
              testId={`cert-control-${key}`}
            />
          ))}
        </Section>
      )}

      {validation && (
        <Section title="Transaction Matching" testId="cert-matching-block">
          <div className="cert-matching-counts">
            {Object.entries(validation.matching.counts || {}).map(([k, v]) => (
              <div key={k} className={"cert-count " + (v > 0 && k !== "matched" ? "bad" : v > 0 ? "ok" : "idle")}>
                <div className="cert-count-num">{v}</div>
                <div className="cert-count-lbl">{k.replace(/_/g, " ")}</div>
              </div>
            ))}
          </div>
          {validation.matching.exceptions?.length > 0 && (
            <div className="cert-exceptions">
              <div className="muted" style={{ fontSize: 12, margin: "8px 0 4px" }}>
                Exceptions ({validation.matching.exceptions.length}):
              </div>
              <div className="cert-exception-list">
                {validation.matching.exceptions.slice(0, 20).map((ex, i) => (
                  <div key={i} className="cert-exception" data-testid={`cert-exception-${i}`}>
                    <span className={"cert-check-flag bad"}>{ex.type.replace(/_/g, " ")}</span>
                    <span className="mono" style={{ fontSize: 12 }}>
                      {ex.date} · {ex.dr_cr} · ${fmt.currency(ex.amount)}
                    </span>
                    <span className="muted" style={{ fontSize: 12 }}>
                      {ex.description?.slice(0, 60)}
                    </span>
                  </div>
                ))}
                {validation.matching.exceptions.length > 20 && (
                  <div className="muted" style={{ fontSize: 11 }}>
                    …and {validation.matching.exceptions.length - 20} more.
                  </div>
                )}
              </div>
            </div>
          )}
        </Section>
      )}

      {validation && (
        <Section title="Running Balance Replay" testId="cert-running-balance-block">
          <div className="cert-check-row">
            <span className={"cert-check-flag " + (validation.running_balance.passed ? "ok" : "bad")}>
              {validation.running_balance.passed ? "PASS" : "FAIL"}
            </span>
            <span className="cert-check-label">Replay reproduces statement closing</span>
            <span className="mono" style={{ fontSize: 12 }}>
              calc {fmt.currency(validation.running_balance.final_calculated)} vs
              stmt {fmt.currency(validation.running_balance.final_statement)}
              {" "}(Δ {fmt.currency(validation.running_balance.closing_difference)})
            </span>
          </div>
          {validation.running_balance.first_divergence && (
            <div className="cert-first-divergence" data-testid="cert-first-divergence">
              <div className="muted" style={{ fontSize: 12, marginTop: 8 }}>First divergence:</div>
              <div className="mono" style={{ fontSize: 12 }}>
                seq #{validation.running_balance.first_divergence.statement_sequence} ·
                {" "}{validation.running_balance.first_divergence.date} ·
                {" "}"{validation.running_balance.first_divergence.description}"
              </div>
              <div className="mono" style={{ fontSize: 12, marginTop: 2 }}>
                expected ${fmt.currency(validation.running_balance.first_divergence.expected_balance)} ·
                calculated ${fmt.currency(validation.running_balance.first_divergence.calculated_balance)} ·
                Δ ${fmt.currency(validation.running_balance.first_divergence.difference)}
              </div>
              <div style={{ fontSize: 12, marginTop: 6, color: "var(--blocker, #b00)" }}>
                Reason: {validation.running_balance.first_divergence.reason}
              </div>
            </div>
          )}
        </Section>
      )}

      {validation?.variance?.ranked_causes?.length > 0 && (
        <Section title="Ranked Variance Causes" testId="cert-variance-block">
          <ol style={{ margin: 0, paddingLeft: 20, fontSize: 13 }}>
            {validation.variance.ranked_causes.map((c, i) => (
              <li key={i} data-testid={`cert-variance-cause-${i}`}>
                <strong>{c.kind.replace(/_/g, " ")}</strong>
                {c.count != null && <> ({c.count} occurrence{c.count === 1 ? "" : "s"})</>}
                {c.note && <div className="muted" style={{ fontSize: 12 }}>{c.note}</div>}
              </li>
            ))}
          </ol>
        </Section>
      )}

      {certification?.override_reason && (
        <Section title="Controller Override" testId="cert-override-block">
          <div className="cert-override-reason">"{certification.override_reason}"</div>
          <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>
            by {certification.certified_by} · {fmt.dateTime(certification.created_at)}
          </div>
        </Section>
      )}

      {validation?.summary && (
        <div className="cert-summary" data-testid="cert-summary">
          <div className="muted" style={{ fontSize: 12, marginBottom: 4 }}>Summary</div>
          <div style={{ fontSize: 13 }}>{validation.summary}</div>
        </div>
      )}
    </div>
  );
}
